/**
 * TenantsAdmin — Maker-only dashboard page.
 *
 * Lists every tenant in the platform and provides a one-click
 * "Create tenant" modal that provisions a brand-new holding +
 * holding_admin + (optional) magic-link invite in a single backend
 * call (`POST /api/admin/tenants/create`).
 *
 * Two columns of state shown per row:
 *   • setup_done — green check if the holding_admin has completed
 *     their first-login onboarding wizard; amber "Awaiting setup"
 *     otherwise.
 *   • tenant_type — chip showing holding / startup / family_office /
 *     investor / partner.
 */
import React, { useEffect, useState } from 'react';
import api from '@/lib/api';
import { toast } from 'sonner';
import {
  Building2, Plus, X, Loader2, Globe, Mail, Sparkles, CheckCircle2,
  Briefcase, Landmark, Users, ExternalLink, Send,
} from 'lucide-react';
import EditableCell from '@/components/tenants/EditableCell';
import useTenantTableActions from '@/components/tenants/useTenantTableActions';

const TENANT_TYPES = [
  { id: 'holding',       label: 'Holding company', icon: Building2,  blurb: 'Multi-entity parent. Full ecosystem.' },
  { id: 'startup',       label: 'Startup',         icon: Sparkles,   blurb: 'Vetted single-entity tenant.' },
  { id: 'family_office', label: 'Family office',   icon: Landmark,   blurb: 'Wealth management + governance.' },
  { id: 'investor',      label: 'Investor fund',   icon: Briefcase,  blurb: 'GP/LP fund administration.' },
  { id: 'partner',       label: 'Partner',         icon: Users,      blurb: 'External service provider.' },
];

const PLAN_OPTIONS = [
  { value: 'trial',   label: 'Trial' },
  { value: 'monthly', label: 'Monthly' },
  { value: 'annual',  label: 'Annual' },
];

// Module-level cell formatters — keeping these out of the parent
// render keeps the strict `no-unstable-nested-components` lint happy
// and means the DOM nodes aren't torn down + recreated on every render
// of the table.
const formatTypeCell = (v) => <TypePill tenantType={v} />;
const formatPlanCell = (v) => <span>{v}</span>;
const formatWebsiteCell = (v) => (
  v
    ? (
      <span className="text-amber-300 inline-flex items-center gap-1">
        {v.replace(/^https?:\/\//, '').slice(0, 26)}
        <a
          href={v.startsWith('http') ? v : `https://${v}`}
          target="_blank"
          rel="noreferrer"
          onClick={(e) => e.stopPropagation()}
          className="hover:text-amber-200"
        ><ExternalLink size={9}/></a>
      </span>
    )
    : <span className="text-zinc-600">—</span>
);

const TYPE_COLORS = {
  holding:       'bg-violet-500/15 text-violet-300 border-violet-500/40',
  startup:       'bg-amber-500/15 text-amber-300 border-amber-500/40',
  family_office: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/40',
  investor:      'bg-sky-500/15 text-sky-300 border-sky-500/40',
  partner:       'bg-zinc-500/15 text-zinc-300 border-zinc-500/40',
};

function StatusPill({ setupDone, inviteStatus }) {
  if (setupDone) {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] uppercase tracking-wider font-bold border border-emerald-500/40 bg-emerald-500/10 text-emerald-400">
        <CheckCircle2 size={10} /> Setup done
      </span>
    );
  }
  if (inviteStatus === 'invited') {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] uppercase tracking-wider font-bold border border-sky-500/40 bg-sky-500/10 text-sky-300">
        <Send size={10} /> Invited
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] uppercase tracking-wider font-bold border border-amber-500/40 bg-amber-500/10 text-amber-400">
      ⏳ Awaiting setup
    </span>
  );
}

function TypePill({ tenantType }) {
  const t = TENANT_TYPES.find((x) => x.id === tenantType);
  const cls = TYPE_COLORS[tenantType] || TYPE_COLORS.partner;
  return (
    <span className={`inline-block px-1.5 py-0.5 rounded text-[10px] uppercase tracking-wider font-bold border ${cls}`}>
      {t?.label || tenantType || '—'}
    </span>
  );
}

function CreateTenantModal({ open, onClose, onCreated }) {
  const [form, setForm] = useState({
    brand_name: '',
    tenant_type: 'holding',
    owner_email: '',
    owner_name: '',
    website: '',
    primary_color: '#D4AF37',
    plan: 'trial',
    send_invite: true,
  });
  const [busy, setBusy] = useState(false);

  if (!open) return null;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post('/admin/tenants/create', form);
      toast.success(
        data.invite_sent
          ? `${data.brand_name} created — invite emailed to ${data.owner_email}`
          : `${data.brand_name} created — Maker will assign users manually.`,
      );
      onCreated?.(data);
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.detail || err.message || 'Failed to create tenant.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[80] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="create-tenant-modal"
    >
      <div
        className="w-full max-w-xl rounded-xl border border-white/10 bg-zinc-950 text-white p-6 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-amber-400">Maker · provision tenant</div>
            <h2 className="text-xl font-bold mt-1">Create a new tenant</h2>
          </div>
          <button onClick={onClose} className="text-zinc-400 hover:text-white"><X size={18} /></button>
        </div>

        <form onSubmit={submit} className="space-y-4">
          {/* Type picker */}
          <div>
            <label className="text-[10px] uppercase tracking-wider font-bold text-zinc-400 mb-1.5 block">Type</label>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {TENANT_TYPES.map((t) => {
                const Icon = t.icon;
                const active = form.tenant_type === t.id;
                return (
                  <button
                    key={t.id}
                    type="button"
                    onClick={() => setForm({ ...form, tenant_type: t.id })}
                    data-testid={`tenant-type-${t.id}`}
                    className={`text-left p-2.5 rounded border transition-colors ${
                      active ? 'border-amber-500 bg-amber-500/10' : 'border-zinc-700 hover:border-zinc-500'
                    }`}
                  >
                    <div className="text-[12px] font-bold flex items-center gap-1.5">
                      <Icon size={12} className={active ? 'text-amber-400' : 'text-zinc-400'} />
                      {t.label}
                    </div>
                    <div className="text-[10px] text-zinc-400 mt-0.5">{t.blurb}</div>
                  </button>
                );
              })}
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <label className="block">
              <span className="text-[10px] uppercase tracking-wider font-bold text-zinc-400 mb-1 block">Brand name *</span>
              <input
                required
                value={form.brand_name}
                onChange={(e) => setForm({ ...form, brand_name: e.target.value })}
                data-testid="tenant-brand-name"
                className="w-full bg-zinc-900 border border-zinc-700 rounded px-3 py-2 text-sm focus:outline-none focus:border-amber-500"
                placeholder="e.g. ACME Holdings"
              />
            </label>
            <label className="block">
              <span className="text-[10px] uppercase tracking-wider font-bold text-zinc-400 mb-1 block">Primary color</span>
              <div className="flex items-center gap-2">
                <input
                  type="color"
                  value={form.primary_color}
                  onChange={(e) => setForm({ ...form, primary_color: e.target.value })}
                  className="w-10 h-9 rounded border border-zinc-700 bg-zinc-900 cursor-pointer"
                />
                <input
                  value={form.primary_color}
                  onChange={(e) => setForm({ ...form, primary_color: e.target.value })}
                  className="flex-1 bg-zinc-900 border border-zinc-700 rounded px-3 py-2 text-sm font-mono"
                />
              </div>
            </label>
          </div>

          <label className="block">
            <span className="text-[10px] uppercase tracking-wider font-bold text-zinc-400 mb-1 block">Owner (holding_admin) email *</span>
            <div className="relative">
              <Mail size={13} className="absolute left-2.5 top-2.5 text-zinc-500" />
              <input
                required
                type="email"
                value={form.owner_email}
                onChange={(e) => setForm({ ...form, owner_email: e.target.value })}
                data-testid="tenant-owner-email"
                className="w-full bg-zinc-900 border border-zinc-700 rounded pl-8 pr-3 py-2 text-sm focus:outline-none focus:border-amber-500"
                placeholder="owner@company.com"
              />
            </div>
          </label>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <label className="block">
              <span className="text-[10px] uppercase tracking-wider font-bold text-zinc-400 mb-1 block">Owner name</span>
              <input
                value={form.owner_name}
                onChange={(e) => setForm({ ...form, owner_name: e.target.value })}
                className="w-full bg-zinc-900 border border-zinc-700 rounded px-3 py-2 text-sm focus:outline-none focus:border-amber-500"
                placeholder="Optional"
              />
            </label>
            <label className="block">
              <span className="text-[10px] uppercase tracking-wider font-bold text-zinc-400 mb-1 block">Website</span>
              <div className="relative">
                <Globe size={13} className="absolute left-2.5 top-2.5 text-zinc-500" />
                <input
                  type="url"
                  value={form.website}
                  onChange={(e) => setForm({ ...form, website: e.target.value })}
                  data-testid="tenant-website"
                  className="w-full bg-zinc-900 border border-zinc-700 rounded pl-8 pr-3 py-2 text-sm focus:outline-none focus:border-amber-500"
                  placeholder="https://… (optional)"
                />
              </div>
            </label>
          </div>

          <label className="flex items-center gap-2 text-[12px] text-zinc-300 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={form.send_invite}
              onChange={(e) => setForm({ ...form, send_invite: e.target.checked })}
              data-testid="tenant-send-invite"
              className="accent-amber-500"
            />
            Send a magic-link sign-in invite to {form.owner_email || 'the owner'} now
          </label>

          <div className="flex items-center justify-end gap-2 pt-2 border-t border-white/5">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 rounded text-zinc-300 hover:bg-zinc-800 text-sm"
            >Cancel</button>
            <button
              type="submit"
              disabled={busy}
              data-testid="tenant-submit"
              className="px-4 py-2 rounded bg-amber-500 hover:bg-amber-400 text-black text-sm font-bold disabled:opacity-50 inline-flex items-center gap-1.5"
            >
              {busy && <Loader2 size={14} className="animate-spin" />}
              Create tenant
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function TenantsAdmin() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [pendingInviteId, setPendingInviteId] = useState(null);

  const { patchTenant, resendInvite } = useTenantTableActions({
    rows, setRows, setPendingInviteId,
  });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        const { data } = await api.get('/admin/tenants/list', { params: { limit: 200 } });
        if (!cancelled) setRows(data.rows || []);
      } catch (e) {
        if (!cancelled) toast.error(e?.response?.data?.detail || e.message || 'Failed to load tenants');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Manual reload used by the create-tenant modal after success.
  const reload = async () => {
    try {
      setLoading(true);
      const { data } = await api.get('/admin/tenants/list', { params: { limit: 200 } });
      setRows(data.rows || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message || 'Failed to load tenants');
    } finally {
      setLoading(false);
    }
  };

  // (action handlers moved into useTenantTableActions hook)

  return (
    <div className="bg-zinc-950 min-h-full text-white px-6 py-8 space-y-6" data-testid="tenants-admin">
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-amber-400 mb-1">
            <Building2 size={11} className="inline -mt-0.5 mr-1" />
            Maker · tenants
          </div>
          <h1 className="text-3xl font-bold">All tenants</h1>
          <p className="text-zinc-400 text-sm mt-2 max-w-2xl">
            Provision new holding companies, startups, family offices, investor funds, and partners.
            Each new tenant is created with <code className="text-[11px] font-mono px-1 py-0.5 rounded bg-zinc-800 text-amber-300">setup_done=false</code>{' '}
            so the owner&apos;s first sign-in triggers the brand setup wizard.
          </p>
        </div>
        <button
          type="button"
          onClick={() => setModalOpen(true)}
          data-testid="create-tenant-btn"
          className="inline-flex items-center gap-2 px-4 py-2 rounded bg-amber-500 hover:bg-amber-400 text-black text-sm font-bold"
        >
          <Plus size={14} />
          Create tenant
        </button>
      </div>

      <div className="rounded-xl border border-white/10 bg-zinc-950/40 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-[12.5px]" data-testid="tenants-table">
            <thead className="bg-zinc-900/60 text-zinc-400 text-[10px] uppercase tracking-wider">
              <tr>
                <th className="text-left px-2 py-2.5 font-bold">Brand</th>
                <th className="text-left px-2 py-2.5 font-bold">Type</th>
                <th className="text-left px-2 py-2.5 font-bold">Owner</th>
                <th className="text-left px-2 py-2.5 font-bold">Plan</th>
                <th className="text-left px-2 py-2.5 font-bold">Status</th>
                <th className="text-left px-2 py-2.5 font-bold hidden 2xl:table-cell">Website</th>
                <th className="text-left px-2 py-2.5 font-bold">Created</th>
                <th className="text-right px-2 py-2.5 font-bold">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading && rows.length === 0 && (
                <tr><td colSpan={8} className="px-3 py-8 text-center text-zinc-500">Loading…</td></tr>
              )}
              {!loading && rows.length === 0 && (
                <tr><td colSpan={8} className="px-3 py-8 text-center text-zinc-500">
                  No tenants yet. Click <strong>Create tenant</strong> above to provision your first.
                </td></tr>
              )}
              {rows.map((t) => (
                <tr key={t.tenant_id} className="border-t border-white/5 hover:bg-zinc-900/30 align-top" data-testid={`tenant-row-${t.tenant_id}`}>
                  {/* BRAND — editable brand_name, fixed tenant_id */}
                  <td className="px-2 py-2">
                    <div className="flex items-center gap-2">
                      {t.logo_url
                        ? <img src={t.logo_url} alt="" className="h-6 w-6 rounded object-cover shrink-0" />
                        : <div className="h-6 w-6 rounded bg-zinc-700 flex items-center justify-center text-[9px] font-bold shrink-0">{(t.brand_name || '?')[0]}</div>}
                      <div className="min-w-0">
                        <div className="font-bold text-white">
                          <EditableCell
                            value={t.brand_name}
                            onSave={(v) => patchTenant(t.tenant_id, { brand_name: v })}
                            testId={`tenant-brand-${t.tenant_id}`}
                            ariaLabel="Brand name"
                            className="max-w-[160px]"
                          />
                        </div>
                        <div className="text-[10px] font-mono text-zinc-500">{t.tenant_id}</div>
                      </div>
                    </div>
                  </td>
                  {/* TYPE — editable select */}
                  <td className="px-2 py-2">
                    <EditableCell
                      type="select"
                      value={t.tenant_type || 'holding'}
                      options={TENANT_TYPES.map((x) => ({ value: x.id, label: x.label }))}
                      onSave={(v) => patchTenant(t.tenant_id, { tenant_type: v })}
                      testId={`tenant-type-${t.tenant_id}`}
                      ariaLabel="Tenant type"
                      formatDisplay={formatTypeCell}
                    />
                  </td>
                  {/* OWNER — name + email, both editable */}
                  <td className="px-3 py-2">
                    <div className="text-white">
                      <EditableCell
                        value={t.owner_name}
                        onSave={(v) => patchTenant(t.tenant_id, { owner_name: v })}
                        placeholder="Add owner name"
                        testId={`tenant-owner-name-${t.tenant_id}`}
                        ariaLabel="Owner name"
                      />
                    </div>
                    <div className="text-[10.5px] text-zinc-500">
                      <EditableCell
                        value={t.owner_email}
                        onSave={(v) => patchTenant(t.tenant_id, { owner_email: v })}
                        placeholder="Add owner email"
                        testId={`tenant-owner-email-${t.tenant_id}`}
                        ariaLabel="Owner email"
                      />
                    </div>
                  </td>
                  {/* PLAN — editable select */}
                  <td className="px-3 py-2 text-zinc-300 uppercase text-[10.5px] font-bold">
                    <EditableCell
                      type="select"
                      value={t.plan || 'trial'}
                      options={PLAN_OPTIONS}
                      onSave={(v) => patchTenant(t.tenant_id, { plan: v })}
                      testId={`tenant-plan-${t.tenant_id}`}
                      ariaLabel="Subscription plan"
                      formatDisplay={formatPlanCell}
                    />
                  </td>
                  {/* STATUS — derived from setup_done + invite_status */}
                  <td className="px-2 py-2">
                    <StatusPill setupDone={!!t.setup_done} inviteStatus={t.invite_status} />
                  </td>
                  {/* WEBSITE — editable text. Hidden below 2xl so the
                      Created + Actions columns never get pushed past the
                      edge of the screen on smaller/zoomed displays. */}
                  <td className="px-2 py-2 text-zinc-400 hidden 2xl:table-cell">
                    <EditableCell
                      value={t.website}
                      onSave={(v) => patchTenant(t.tenant_id, { website: v })}
                      placeholder="Add website"
                      testId={`tenant-website-${t.tenant_id}`}
                      ariaLabel="Website URL"
                      formatDisplay={formatWebsiteCell}
                    />
                  </td>
                  <td className="px-2 py-2 text-zinc-500 whitespace-nowrap">
                    {t.created_at ? new Date(t.created_at).toISOString().slice(0, 10) : '—'}
                  </td>
                  {/* ACTIONS — send / re-send invite. Disabled when no
                      owner email is on file so we can't bounce a magic
                      link into the void. */}
                  <td className="px-2 py-2 text-right whitespace-nowrap">
                    {t.setup_done ? (
                      <span className="text-[10px] text-zinc-600 italic">setup complete</span>
                    ) : (
                      <button
                        type="button"
                        onClick={() => resendInvite(t.tenant_id)}
                        disabled={!t.owner_email || pendingInviteId === t.tenant_id}
                        data-testid={`tenant-send-invite-${t.tenant_id}`}
                        title={t.owner_email
                          ? (t.invite_status === 'invited' ? 'Resend setup magic link' : 'Send setup magic link')
                          : 'Add an owner email first'}
                        className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded border border-amber-500/40 bg-amber-500/10 text-amber-300 hover:bg-amber-500/20 disabled:opacity-30 disabled:cursor-not-allowed text-[10px] uppercase tracking-wider font-bold transition"
                      >
                        {pendingInviteId === t.tenant_id
                          ? <Loader2 size={11} className="animate-spin"/>
                          : <Send size={11}/>}
                        {t.invite_status === 'invited' ? 'Resend' : 'Send invite'}
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <CreateTenantModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        onCreated={() => reload()}
      />
    </div>
  );
}
